import React, { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import DynamicColumnTable from 'components/DynamicColumnsTable/DynamicColumnsTable';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';

import { CMASummaryContext } from './CMASummaryContext';
import { generateCMASummaryTableColumns, getAntdRowClassName } from './CMASummaryTableHelper';
import { CMA_SUMMARY_VIEW_FILTER_DISPLAY_COMPETITOR } from './constants';
import { useCMASummaryData } from './customHooks';
import { CMASummaryFilters } from './types';
import { getCMAChildrenRows, prepareCMASummaryDataRowsForAntdTable } from './utils';
import { CommunityType } from '../../types/communities';

const Container = styled.div`
  margin: 15px 20px;
`;

interface Props {
  filteredCompetitors?: CommunityType[];
}

const CMASummaryTable = ({ filteredCompetitors }: Props) => {
  const { community, viewFilters, setCMASummaryData, triggerExpandAllRef, triggerCollapseAllRef } =
    useCommunityViewContext();
  const filters = viewFilters as CMASummaryFilters;

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const { CMASummaryData, isCMADataLoading, isCMADataFetched } = useCMASummaryData({
    community,
    competitors: filteredCompetitors ?? [],
    filters: filters,
  });

  const initializedRef = useRef(false);

  // Reset the guard when inputs change (new community, competitors, or filters)
  useEffect(() => {
    initializedRef.current = false;
  }, [community?.id, filteredCompetitors?.map((c) => c.id).join(','), JSON.stringify(viewFilters)]);

  // Only call setCMA once per fetch cycle
  useEffect(() => {
    if (!initializedRef.current && isCMADataFetched && CMASummaryData && typeof setCMASummaryData === 'function') {
      setCMASummaryData(CMASummaryData);
      initializedRef.current = true;
    }
  }, [isCMADataFetched, CMASummaryData, setCMASummaryData]);

  const dataSource = prepareCMASummaryDataRowsForAntdTable(expandedRowKeys, CMASummaryData?.slice(1));
  const paginateDynamicColumns =
    filters?.viewOptions?.competitorDisplayMode === CMA_SUMMARY_VIEW_FILTER_DISPLAY_COMPETITOR.PAGINATE;

  const handleExpandAll = () => {
    if (!dataSource || !CMASummaryData) return;

    const allKeys = new Set<string>();

    (dataSource ?? []).forEach((item) => {
      allKeys.add(item.key);

      const childrenRows = getCMAChildrenRows(item.key, CMASummaryData);

      childrenRows.forEach((row) => {
        const childRowMetadata = row[0];
        allKeys.add(childRowMetadata.key);
      });
    });

    setExpandedRowKeys(Array.from(allKeys));
  };

  const handleCollapseAll = () => {
    setExpandedRowKeys([]);
  };

  useEffect(() => {
    triggerExpandAllRef.current = handleExpandAll;
    triggerCollapseAllRef.current = handleCollapseAll;
  }, [dataSource, CMASummaryData]);

  if (isCMADataLoading) {
    return <LoaderRibbed active expanded />;
  }

  return (
    <CMASummaryContext.Provider
      value={{
        data: CMASummaryData,
      }}
    >
      <Container>
        {isCMADataFetched && (
          <DynamicColumnTable
            title="CMA Summary"
            className="cma-summary"
            community={community}
            competitors={filteredCompetitors ?? []}
            dataSource={dataSource}
            enableCustomStickyHeader
            rowKey={'key'}
            expandedRowKeys={expandedRowKeys}
            setExpandedRowKeys={setExpandedRowKeys}
            getRowClassname={getAntdRowClassName}
            generateTableColumns={generateCMASummaryTableColumns}
            paginateDynamicColumns={paginateDynamicColumns}
            showCompetitorAverage={filters?.viewOptions?.averageCompetitors !== false}
          />
        )}
      </Container>
    </CMASummaryContext.Provider>
  );
};

export default CMASummaryTable;
